import React, {useEffect} from "react";
import {Calendar, CalendarProps, dateFnsLocalizer, NavigateAction, SlotInfo, View,} from "react-big-calendar";
import {format, getDay, parse, startOfWeek} from "date-fns";
import {shopNow} from "../../utils/timezone";
import enUS from "date-fns/locale/en-US";
import withDragAndDrop, {EventInteractionArgs, withDragAndDropProps,} from "react-big-calendar/lib/addons/dragAndDrop";

import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./newCalendar.css";
import {MyCalendarEvent, ResourceProps} from "./hooks/useCalendarState";
import {DailyTimeSlotWrapper} from "./ui/dailyTimeSlotWrapper";
import {DailyResourceHeader} from "./ui/dailyResourceHeader";
import {CustomToolbar} from "../components/customToolbar/customToolbar";
import Toolbar from "./ui/Toolbar";
import {CustomEvent, getStatusColor} from "./ui/customEvent/customEvent";
import {find} from "lodash";
import {durationList} from "../../redux/businessServicesSlice";
import {useQuery} from "../../utils/useQuery";
import {Loading} from "../../components/loading/loading";

const locales = {"en-US": enUS};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), {weekStartsOn: 1}),
    getDay,
    locales,
});

export type AllowedView = "day" | "week" | "month";

export interface MyCalendarProps {
    date: Date;
    view: AllowedView;
    events: MyCalendarEvent[];
    blockedTimes?: MyCalendarEvent[];
    setEvents: React.Dispatch<React.SetStateAction<MyCalendarEvent[]>>;

    handleNavigate: (date: Date, view: AllowedView, action: NavigateAction) => void;
    handleViewChange: (view: AllowedView) => void;

    handleEmptySlotClick?: (slotInfo: SlotInfo) => void;
    handleAppointmentClick?: (event: MyCalendarEvent) => void;
    handleNewAppointmentClick: ({startTime, endTime}: {startTime?: Date, endTime?: Date}) => void;
    handleQuickCheckOutClick: () => void;
    handleBlockedClick?: (event: MyCalendarEvent) => void;
    handleOfftimeClick?: (event: MyCalendarEvent) => void;

    handleEventDrop: (args: EventInteractionArgs<MyCalendarEvent>) => void;
    handleEventResize?: (args: EventInteractionArgs<MyCalendarEvent>) => void;

    resourceMap: ResourceProps[];
    isLoading: boolean,
    selectedEmployee: any,
    setSelectedEmployee: any
    employees: any
    shops: any
    shop: any
    selectedEmployeeId:number

}

const DnDCalendar = withDragAndDrop<
    MyCalendarEvent,
    object
>(Calendar as React.ComponentType<
    CalendarProps<MyCalendarEvent, object> & withDragAndDropProps<MyCalendarEvent>
>);


const MyCalendar: React.FC<MyCalendarProps> = ({
                                                   date,
                                                   view,
                                                   events,
                                                   setEvents,
                                                   blockedTimes,
                                                   handleNavigate,
                                                   handleViewChange,
                                                   handleEmptySlotClick,
                                                   handleAppointmentClick,
                                                   handleBlockedClick,
                                                   handleOfftimeClick,
                                                   handleEventDrop,
                                                   handleEventResize,
                                                   resourceMap, isLoading,
                                                   selectedEmployee,
                                                   setSelectedEmployee,
                                                   employees,
                                                   shops,
                                                   shop,
                                                   handleNewAppointmentClick,
                                                   handleQuickCheckOutClick,
                                                   selectedEmployeeId
                                               }) => {

    console.log("inside events", events);
    const query=useQuery()
    const handleSelectEvent = (event: MyCalendarEvent) => {
        switch (event.type) {
            case "appointment":
                handleAppointmentClick?.(event);
                break;
            case "blocked":
                handleBlockedClick?.(event);
                break;
            case "offtime":
                handleOfftimeClick?.(event);
                break;
            default:
                console.warn("Unhandled event type:", event);
        }
    };

    // Custom empty slot click handler
    const handleSelectSlot = (slotInfo: SlotInfo) => {
        handleEmptySlotClick?.(slotInfo);
    };

    // TS-safe handler for `onView`
    const handleCalendarViewChange = (view: View) => {
        if (view === "day" || view === "week" || view === "month") {
            handleViewChange(view);
        }
    };

    const handleCalendarNavigate = (date: Date, view: any, action: NavigateAction) => {
        if (view === "day" || view === "week" || view === "month") {
            handleNavigate(date, view, action);
        }
    };


      const handleResourceChange = (resourceId:number) => {
          const employee = find(employees, { id: resourceId});
          query.delete('employeeId')
          query.set('employeeId',resourceId.toString())
          setSelectedEmployee(employee)

    };







    return (

        <div className="relative h-[90vh] w-full">


            {isLoading && (
                <div className="
                absolute inset-0
                z-50
                flex items-center justify-center
                bg-white/70 backdrop-blur-sm
            ">
                    <Loading
                        label="Loading..."
                        labelClassName="!text-neutral-800 font-medium"
                        lightBackdrop
                        type="dark"
                    />
                </div>
            )}


        <DnDCalendar
            date={date}
            getNow={() => shopNow()} // today's highlight and the now-line follow the shop's clock
            localizer={localizer}
            views={["day", "week", "month"]}
            view={view}
            resources={resourceMap}
            resourceIdAccessor={(resource: any) => resource.resourceId}
            resourceTitleAccessor={(resource: any) => resource.resourceTitle}
            min={new Date(1970, 1, 1, 8, 0, 0)}   // 08:00 AM
            max={new Date(1970, 1, 1, 20, 0, 0)}
            onView={handleCalendarViewChange}
            onNavigate={handleCalendarNavigate}
            events={events}
            backgroundEvents={blockedTimes}
            startAccessor="start"
            endAccessor="end"
            selectable
            resizable={false}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            style={{height: "90vh"}}
            step={15}
            timeslots={1}
            dayLayoutAlgorithm="no-overlap"
            eventPropGetter={eventPropGetter}
            components={{
                toolbar: (props) => (
                    <Toolbar
                        calendarView={view}
                        resources={employees}
                        onViewChange={handleCalendarViewChange}
                        currentDate={date}
                        selectedEmployee={selectedEmployee}
                        setSelectedEmployee={setSelectedEmployee}
                        employees={employees}
                        onResourceChange={handleResourceChange}
                        onNavigate={handleCalendarNavigate}
                        onNewAppointmentInit={handleNewAppointmentClick}
                        onQuickCheckout={handleQuickCheckOutClick}
                        onNewBookingClick={() => {
                        }}
                        selectedResourceId={selectedEmployee?.id}
                        shops={shops}
                        shop={shop}

                    />
                ),
                resourceHeader: DailyResourceHeader,
                timeSlotWrapper: (props) => (
                    <DailyTimeSlotWrapper {...props} isLoading={isLoading}/>
                ),
                event: view === "month"
                    ? undefined : (props) => <CustomEvent props={props} />

            }}
            popup
            popupOffset={{ x: 10, y: 20 }}
        />
        </div>
    );
};

export default MyCalendar;


const eventPropGetter = (event: MyCalendarEvent) => {
    let style: React.CSSProperties = {};

    switch (event.type) {
        case "appointment":
            style = {
                backgroundColor: event?.sale?.status==='OPEN' ? "#fef9c350" :"#67c0a650",
                // backgroundColor: "rgba(225,223,223,0.44)",
                // backgroundColor: "rgba(103,192,166,0.45)",
                color:  "rgba(121,121,121,0.54)",
                // color:  event?.sale?.status ==='OPEN' ? "#854d0e" : "#3b82f6",
border:"none",
                borderRadius: "6px",
                cursor: "pointer",
            };
            break;

        case "blocked":
            style = {
                backgroundColor: event.color || "#9ca3af",
                color: "#000",
                border:"none",
                opacity: 0.4,
                cursor: "not-allowed",
            };
            break;

        case "offtime":
            style = {
                border:"none",
                backgroundColor: event.color || "rgba(128,128,128,0.54)",
                color: "#fff",
                opacity: 0.4,
                cursor: "not-allowed",
            };
            break;

        default:
            style = {backgroundColor: "#ccc"};
    }

    return {style};
};
